import { View, Text, ScrollView, Image, ActivityIndicator } from 'react-native'
import React, { useState } from 'react'
import ScreenWrapper from '@app/hoc/ScreenWrapper'
import { ThemedView, ThemedText, Theme } from '@app/theme'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import { PrivateStackParamList } from '@app/navigationApp/types'
import CommonHeader from '@app/sharedComponents/Header/Header'
import ROUTE from '@app/navigationApp/routes'
import Stepper from '@app/sharedComponents/Stepper/Stepper'
import Button from '@app/sharedComponents/Button/Button'
import AlertIcon from '@app/assets/svg/AlertIcon'
import { getStepper } from '../VerifyBankAccount/services/verifyBankService'
import { useEffect } from 'react'
import { useAppSelector } from '@app/store/hooks'
import { getLastMismatchResponse } from './nameMismatchService'
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet'
import ConfirmReAddSheet from './components/ConfirmReAddSheet'
import { useStepperData } from '@app/hooks/useStepperData'
import { useTheme } from '@shopify/restyle'
import { trackEvent } from '@app/utils'

type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'NAME_MISMATCH'>;

const PAN_IMAGE = 'https://volt-images-alpha.s3.ap-south-1.amazonaws.com/pan1.png'


const NameMismatch = ({ navigation, route }: ScreenProps) => {

    const applicationId = useAppSelector(state => state.auth?.userDetails?.linkedApplications[0]?.applicationId);
    const {stepperData , isLoading: stepperIsLoading} = useStepperData()
    const { colors } = useTheme<Theme>()

    const [nameMismatchResponseData, setNameMismatchResponseData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isConfirmReAddSheetVisible, setIsConfirmReAddSheetVisible] = useState<boolean>(false);
    const mismatchData = route?.params?.mismatchData;
    const accountNumber = route?.params?.accountNumber;
    


    
    const bankMismatchDetails = nameMismatchResponseData?.bankMismatchDetails;
    const nameMatchFailureDetails = nameMismatchResponseData?.bankMismatchDetails?.bankAccountVerificationFailureDetails?.nameMatchFailureDetails ?? mismatchData?.failureDetails.nameMatchFailureDetails;


    const bankCode = bankMismatchDetails?.bankIfscCode.slice(0, 4) ?? route?.params?.bankCode;


    const onContinue = () => {
        setIsConfirmReAddSheetVisible(false);
        setTimeout(() => {
            // wait till bottomsheet closes
            navigation.replace(ROUTE.BANK_ACCOUNT_VERIFICATION);
        }, 500);
    }

    const [logo, setLogo] = useState<string>(`https://volt-images.s3.ap-south-1.amazonaws.com/bank-logos/pngs/${bankCode}.png`)


    useEffect(() => {
        setLogo(`https://volt-images.s3.ap-south-1.amazonaws.com/bank-logos/pngs/${bankCode}.png`)
        trackEvent('BANK_DEVIATION_FOUND_PAGE_VIEWED')
    }, [bankMismatchDetails])
  



    


    const fetchNameMismatchResponse = async () => {
        try {
            const response = await getLastMismatchResponse({ applicationId });
            setNameMismatchResponseData(response);
        } catch (error) {
            console.error(error);
        }
    }


    useEffect(() => {
        if(mismatchData)  {
            setIsLoading(false);
            return;
        };
        setIsLoading(true);
        // lets call these parallelly
        Promise.all([fetchNameMismatchResponse()]).then(() => {
        }).then(() => {
            setIsLoading(false);
        });
    }, [mismatchData]);


    if (isLoading || stepperIsLoading) {
        return <ThemedView flex={1} bg='bg-surface-primary' justifyContent='center' alignItems='center'>
            <ActivityIndicator size={12} />
        </ThemedView>
    }

    return (
        <ScreenWrapper>
            <ThemedView flex={1} bg='bg-surface-primary'>
                <CommonHeader
                    navigation={navigation}
                    pageName={route.name}
                    showNeedHelp={true}
                    hideBottomBorder={true}
                    label='Link bank account'
                />
                <Stepper stepperData={stepperData} />
                <ScrollView>
                    <ThemedView padding='lg' paddingTop='2xl'>
                        <ThemedView alignItems='center' mb='lg'>
                            <AlertIcon color={colors['icon-error']} />
                        </ThemedView>
                        <ThemedText mb='lg' textAlign='center' variant='h5s' color='text-primary'>{nameMatchFailureDetails?.description}</ThemedText>
                        <ThemedView flexDirection='row' alignItems='center' justifyContent='space-between' mt='2xl' borderBottomColor='border-primary' borderBottomWidth={1} pb='lg'>
                            <ThemedView>
                                <ThemedText variant='b4r' mb='sm' color='text-secondary'>{nameMatchFailureDetails?.source?.description}</ThemedText>
                                <ThemedText variant='b2s' color='text-primary'>{nameMatchFailureDetails?.source?.value}</ThemedText>
                            </ThemedView>
                            <Image source={{ uri: PAN_IMAGE }} resizeMode='contain' style={{ width: 42, height: '100%' }} />
                        </ThemedView>
                        <ThemedView flexDirection='row' alignItems='center' justifyContent='space-between' mt='lg' pb='md'>
                            <ThemedView>
                                <ThemedText variant='b4m' mb='sm' color='text-secondary'>{nameMatchFailureDetails?.target?.description}</ThemedText>
                                <ThemedText variant='b2s' color='text-primary'>{nameMatchFailureDetails?.target?.value}</ThemedText>
                            </ThemedView>
                            <Image source={{ uri: logo }} onError={() => {
                                setLogo(`https://volt-images.s3.ap-south-1.amazonaws.com/bank-logos/pngs/default.png`)
                            }} style={{ width: 40, height: 40 }} />
                        </ThemedView>
                    </ThemedView>

                </ScrollView>
                <ThemedView
                    style={{ width: "100%" }}
                    bg='bg-surface-primary'
                    padding='lg'
                    borderTopWidth={1}
                    borderTopColor='border-primary'
                >
                    <ThemedText textAlign='center' mb='lg' variant='b4r' color='text-secondary'>Does this bank account belong to you?
                    </ThemedText>
                    <ThemedView mb='md'>
                        <Button
                            label="Yes, It’s my bank account"
                            type="SOLID"
                            handlePress={() => navigation.navigate(ROUTE.SUPPORTING_DOCUMENT , { accountNumber : bankMismatchDetails?.bankAccountNumber ?? accountNumber })}
                            size='MD'
                            bg={colors['bg-action-primary']}
                            textColor={colors['text-primary']}
                            eventName='BANK_DEVIATION_FOUND_YES'
                        />
                    </ThemedView>
                    <Button
                        label="No, I added wrong bank account"
                        eventName='BANK_DEVIATION_FOUND_NO'
                        type="OUTLINED"
                        handlePress={() => { setIsConfirmReAddSheetVisible(true) }}
                        size='MD'
                    />
                </ThemedView>

            </ThemedView>
            <BottomSheetContainer
                showCloseIcon={true}
                isVisible={isConfirmReAddSheetVisible}
                close={() => { setIsConfirmReAddSheetVisible(false) }}
                onClose={() => { setIsConfirmReAddSheetVisible(false) }}
                dragComponent={null}
                containerStyle={{}}
                containsTextInput={false}
                borderTopRightRadius={16}
                borderTopLeftRadius={16}
            >
                <ConfirmReAddSheet customerName={nameMatchFailureDetails?.source?.value} onContinue={onContinue} />
            </BottomSheetContainer>
        </ScreenWrapper>
    )
}

export default NameMismatch